import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { Subscription } from 'rxjs/Subscription';
import { ColDef, ColGroupDef, GridOptions, SelectionChangedEvent } from 'ag-grid';
import { ColumnState } from 'ag-grid/dist/lib/columnController/columnController';
import { anchorPopupToSource } from '../../../core/helpers/ag-grid-popup.util';
import { BooleanCheckComponent } from '../../A2-utils/grid-components/boolean-check/boolean-check.component';
import { SweetAlertHelper } from 'app/core/helpers/sweet-alert-helper.service';
import { ICurrentModuleActions } from 'app/core/interfaces/rules-by-code.interface';
import { RulesService } from 'app/pages/rules/rules.service';
import { ReservationService } from '../reservations.service';
import { IResponseCancelTrips } from '../../../core/interfaces/cancel-reason.interface';
import { GridLayoutStore, moduleActionIdOf } from '../../../utils/grid-layout-menu/grid-layout-store';

@Component({
  selector: 'app-trip-history',
  templateUrl: './trip-history.component.html',
  styleUrls: ['./trip-history.component.scss'],
  providers: [GridLayoutStore]
})
export class TripHistoryComponent implements OnInit, OnDestroy, OnChanges {
  public selectedTrips: number[] = [];
  riderid: number;
  @Input() denial = false;
  @Input() showCopyTrip = false;
  @Output() tripid: EventEmitter < any > = new EventEmitter < any > ();
  protected showCancel = true;
  protected showClosed = false;
  protected futureTrips = false;
  protected todaystrips = false;
  protected isNonMedicalTrips = false;
  protected totalRows: number;
  protected grid: GridOptions;
  private dataTrip: {};
  private dataCurrentModule: ICurrentModuleActions = {};
  private layoutConfig: ColumnState[] | undefined;
  observers$: Subscription[] = [];
  @Output() dataTripEmit: EventEmitter<any> = new EventEmitter<any>();

  get showCopyTripButton(): boolean {
    return !this.denial && this.showCopyTrip;
  }

  constructor(
    protected service: ReservationService,
    private rulesService: RulesService,
    private sweetAlertHelper: SweetAlertHelper,
    private layoutStore: GridLayoutStore
  ) {
    const self = this;
    this.grid = < GridOptions > {};
    this.grid.enableColResize = true;
    this.grid.enableSorting = true;
    this.configureGrid();
    this.observers$.push(
    this.service.riderSelected$.subscribe(rider => {
      this.riderid = rider;
      this.getTrips(rider);
    }),
    this.service.reloadTrips$.subscribe(res => this.getTrips(this.riderid))
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    const denial = changes['denial'];
    if (denial && denial.currentValue !== denial.previousValue) {
      this.updateCheckColumnVisibility();
    }
  }
  ngOnInit() {
    this.loadModuleLayout();
  }
  ngOnDestroy(): void {
    this.observers$.forEach(item => item.unsubscribe());
  }

  public getTrips(riderid) {
    if ( riderid === null || riderid === undefined) {
      return;
    }
    this.riderid = riderid;
    const dataParams = {
      'riderid': this.riderid,
      'isshowcancels': this.showCancel,
      'isshowclosed': this.showClosed,
      'isshowfuturetrips': this.futureTrips,
      'isshowtodaytrips': this.todaystrips,
      'standingorderonly': false,
      'isnonmedicaltrips': this.isNonMedicalTrips
    };

    this.service.getTripsListByRider(dataParams).subscribe(
      data => {
        if (this.grid) {
          if (this.grid.api) {
            const filters = this.grid.api.getFilterModel();
            this.grid.api.setRowData(data);
            this.grid.api.setFilterModel(filters);
            this.totalRows = this.grid.api.getDisplayedRowCount();
          }
        }
      }
    );
    this.grid.api.deselectAll();
  }
  /**
   * @description
   */
  private configureGrid() {
    this.grid.columnDefs = this.createColumns();
    this.grid.postProcessPopup = (params) => {
      if (params && params.ePopup) {
        anchorPopupToSource(
          params.ePopup as HTMLElement,
          params.eventSource as HTMLElement,
        );
      }
    };

    this.grid.onGridReady = () => {
      if (this.layoutConfig) {
        this.applyLayout();
      } else if (this.grid.api) {
        this.grid.api.sizeColumnsToFit();
      }
      this.updateCheckColumnVisibility();
    };
    this.grid.onSelectionChanged = (event: SelectionChangedEvent) => {
      this.dataTrip = {};
      this.selectedTrips = this.getSelectedTripsId();
    };
    this.detectFiltersGrid();
  }

  private loadModuleLayout(): void {
    this.observers$.push(
      this.rulesService.getRulerByCode('app-new-reservation').subscribe(
        (data) => {
          const actions = {};
          if (data && data.actions) {
            data.actions.forEach((action) => {
              actions[action.actionname] = action;
            });
          }
          this.dataCurrentModule = {
            moduleid: data ? data.moduleid : 0,
            actions: actions
          };
          this.layoutStore.set({
            grid: this.grid,
            moduleActionId: moduleActionIdOf(this.dataCurrentModule, 'Read'),
            title: 'Trip History',
            buttonClass: 'm-btn m-btn-primary m-btn-circle m-btn-circle-small-medium'
          });
          this.getLayout();
        },
        (error) => this.sweetAlertHelper.captureException(error)
      )
    );
  }

  private getLayout(): void {
    if (!this.dataCurrentModule.moduleid) {
      return;
    }
    this.observers$.push(
      this.service.getRoleActionByModule(this.dataCurrentModule.moduleid).subscribe(
        (data) => {
          this.layoutConfig = undefined;
          if (data && data.length) {
            data.forEach((val) => {
              if (val && val.action && val.action.description === 'Read' && val.layout && val.layout.config) {
                try {
                  this.layoutConfig = JSON.parse(val.layout.config) as ColumnState[];
                } catch (error) {
                  this.layoutConfig = undefined;
                }
              }
            });
          }
          this.applyLayout();
        },
        (error) => this.sweetAlertHelper.captureException(error)
      )
    );
  }

  private applyLayout(): void {
    if (this.layoutConfig && this.grid && this.grid.columnApi) {
      this.grid.columnApi.setColumnState(this.layoutConfig);
      this.updateCheckColumnVisibility();
    }
  }

  saveLayout(): void {
    const layoutAction = this.dataCurrentModule.actions && this.dataCurrentModule.actions['Read'];
    if (!this.grid || !this.grid.columnApi || !layoutAction) {
      this.sweetAlertHelper.createCustomAlert({
        title: 'Save Layout',
        text: 'The reservation module has no Read action to store this layout.',
        type: 'warning'
      });
      return;
    }
    const data = {
      moduleactionid: layoutAction.moduleactionid,
      config: JSON.stringify(this.grid.columnApi.getColumnState())
    };
    this.observers$.push(
      this.service.postSaveLayout(data).subscribe(
        () => {
          this.sweetAlertHelper.createCustomAlert({
            title: 'Success',
            text: 'Layout saved successfully',
            type: 'success'
          });
        },
        (error) => this.sweetAlertHelper.captureException(error)
      )
    );
  }

  private updateCheckColumnVisibility(): void {
    if (this.grid && this.grid.columnApi) {
      this.grid.columnApi.setColumnVisible('check', !this.denial);
    }
  }

  private detectFiltersGrid() {
    this.grid.onFilterChanged = () => {
      this.selectedTrips = this.getSelectedTripsId();
      this.totalRows = this.grid.api.getDisplayedRowCount();
    };
  }

  /**
   * @description
   * @param event
   */
  public onChangeCheck(event) {
    this.getTrips(this.riderid);
  }

  /**
   * @description
   * @param event   */
  public onRowDoubleClickedTrip(event) {
    const dataEmit = {
      tripid: event.data.tripid,
      reservationid: event.data.reservationid
    };
    this.tripid.emit(dataEmit);
  }

  onRowCellClicked(e) {
    const dataTrip = {
      tripId: e.data.tripid,
      reservationId: e.data.reservationid
    };

    this.dataTrip = dataTrip;
  }

  copyTrip() {
    this.dataTripEmit.emit(this.dataTrip);
  }

  /**
   * @description retorna seleccionados despues de filtro
   */
  private getSelectedTripsId(): number[] {
    const tripsid = [];
    this.grid.api.forEachNodeAfterFilter((node) => {
      if (node.isSelected()) {
        tripsid.push(node.data.tripid);
          this.dataTrip = {
            tripId: node.data.tripid,
            reservationId: node.data.reservationid
          };
      }
    });
    return tripsid;
  }
  /**
   * @description crea las colunas de la grilla
   */
  private createColumns(): (ColDef | ColGroupDef)[] {
    const data: (ColDef | ColGroupDef)[] = [
      {
        headerName: 'Check',
        colId: 'check',
        width: 50,
        checkboxSelection: true,
        headerCheckboxSelection: true,
        suppressFilter: true,
      },
      {
        headerName: 'Confirmed',
        field: 'tripconfirmed',
        colId: 'ripconfirmed',
        cellRendererFramework: BooleanCheckComponent,
        width: 70
      },
      {
        headerName: 'Date',
        field: 'dateofserviceformatted',
        colId: 'date',
        width: 90,
        cellRenderer: function (params) {
          const dateFormated = params.data.dateofserviceformatted;
          const splitDate = dateFormated.split('-');
          const dataShow = splitDate[1] + '-' + splitDate[2] + '-' + splitDate[0];
          return dataShow;
        }
      },
      {
        headerName: 'Reservation',
        field: 'consecutive',
        colId: 'consecutive',
        width: 90
      },
      {
        headerName: 'Company',
        field: 'broker.speedcode',
        colId: 'broker',
        width: 90
      },
      {
        headerName: 'Provider',
        field: 'company.speedcode',
        colId: 'company',
        width: 100
      },
      {
        headerName: 'Depot',
        field: 'depot.shortcode',
        colId: 'depot',
        width: 100
      },
      {
        headerName: 'Leg',
        field: 'leg',
        colId: 'leg',
        width: 60
      },
      {
        headerName: 'SO',
        field: 'standingorderid',
        colId: 'leg',
        width: 70
      },
      {
        headerName: 'Status',
        field: 'tripstatus.description',
        colId: 'status',
        width: 60
      },
      {
        headerName: 'Payor',
        field: 'payor.description',
        colId: 'payor',
        width: 100
      },
      {
        headerName: 'Balance',
        field: 'totalamount',
        colId: 'balance',
        width: 100
      },
      {
        headerName: 'PU Time',
        field: 'pickup',
        colId: 'puTime',
        width: 100
      }
    ];
    return data;
  }
  successCancel(event: {success: boolean, response: IResponseCancelTrips}): void {
    if (event.success) {
      this.selectedTrips = [];
      this.grid.api.deselectAll();
      this.getTrips(this.riderid);
    }
  }

  get activateBtn(): boolean {
    return this.selectedTrips.length === 1 ? false : true;
  }
}



// WEBPACK FOOTER //
// ./src/app/pages/trip-reservations/trip-history/trip-history.component.ts